/**
 * Video Cleanup Utilities
 * 
 * This module provides utilities for properly disposing of video elements
 * to prevent WebMediaPlayer overflow errors in Chrome/Chromium browsers.
 * 
 * Chrome limits the number of concurrent WebMediaPlayer instances to ~75.
 * Each <video> element creates a WebMediaPlayer, and without proper cleanup,
 * these accumulate until the browser blocks new media creation.
 */

/**
 * Properly dispose of a video element to release its WebMediaPlayer instance
 * 
 * @param videoElement - The video element to clean up
 * @returns Promise that resolves when cleanup is complete
 */
export const cleanupVideoElement = async (
  videoElement: HTMLVideoElement | null
): Promise<void> => {
  if (!videoElement) return;

  try {
    // Step 1: Pause playback
    videoElement.pause();

    // Step 2: Clear the source to release the media resource
    videoElement.removeAttribute('src');
    videoElement.src = '';

    // Step 3: Remove all source children (if any)
    while (videoElement.firstChild) {
      videoElement.removeChild(videoElement.firstChild);
    }

    // Step 4: Force browser to release the WebMediaPlayer by calling load()
    // This is critical - load() with empty src releases the MediaPlayer
    videoElement.load();

    // Step 5: Small delay to ensure browser processes the cleanup
    await new Promise(resolve => setTimeout(resolve, 50));

    console.log('✅ Video element cleaned up successfully');
  } catch (error) {
    console.error('⚠️ Error during video cleanup:', error);
  }
};

/**
 * Properly dispose of an audio element to release its media resources
 * 
 * @param audioElement - The audio element to clean up
 * @returns Promise that resolves when cleanup is complete
 */
export const cleanupAudioElement = async (
  audioElement: HTMLAudioElement | null
): Promise<void> => {
  if (!audioElement) return;

  try {
    audioElement.pause();
    audioElement.removeAttribute('src');
    audioElement.src = '';
    
    while (audioElement.firstChild) {
      audioElement.removeChild(audioElement.firstChild);
    }
    
    audioElement.load();
    
    await new Promise(resolve => setTimeout(resolve, 50));
    
    console.log('✅ Audio element cleaned up successfully');
  } catch (error) {
    console.error('⚠️ Error during audio cleanup:', error);
  }
};

/**
 * Switch video source with proper cleanup to prevent MediaPlayer accumulation
 * 
 * This is critical for video segment switching - the old MediaPlayer must be
 * released before loading a new source.
 * 
 * @param videoElement - The video element to update
 * @param newSource - The new video source URL
 * @param currentTime - Optional time to seek to after loading
 * @returns Promise that resolves when source is switched and ready
 */
export const switchVideoSource = async (
  videoElement: HTMLVideoElement | null,
  newSource: string,
  currentTime?: number
): Promise<void> => {
  if (!videoElement) return;

  try {
    // Step 1: Clean up old source
    videoElement.pause();
    videoElement.src = '';
    videoElement.load();

    // Step 2: Wait for cleanup to process
    await new Promise(resolve => setTimeout(resolve, 50));

    // Step 3: Set new source
    videoElement.src = newSource;
    videoElement.load();

    // Step 4: Wait for new source to be ready
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Video load timeout'));
      }, 10000);

      const handleCanPlay = () => {
        clearTimeout(timeout);
        videoElement.removeEventListener('canplay', handleCanPlay);
        videoElement.removeEventListener('error', handleError);
        resolve();
      };

      const handleError = (e: ErrorEvent) => {
        clearTimeout(timeout);
        videoElement.removeEventListener('canplay', handleCanPlay);
        videoElement.removeEventListener('error', handleError);
        reject(e);
      };

      videoElement.addEventListener('canplay', handleCanPlay);
      videoElement.addEventListener('error', handleError);
    });

    // Step 5: Seek to specified time if provided
    if (currentTime !== undefined) {
      videoElement.currentTime = currentTime;
    }

    console.log('✅ Video source switched successfully:', newSource);
  } catch (error) {
    console.error('⚠️ Error switching video source:', error);
    throw error;
  }
};

/**
 * Debug utility to count active video elements in the DOM
 * Use this to monitor for MediaPlayer leaks
 * 
 * @returns Object with counts of video and audio elements
 */
export const countMediaElements = (): { videos: number; audios: number } => {
  const videos = document.querySelectorAll('video').length;
  const audios = document.querySelectorAll('audio').length;
  
  return { videos, audios };
};

/**
 * Start monitoring media element counts (for debugging)
 * Logs warnings when counts exceed safe thresholds
 * 
 * @param intervalMs - How often to check (default: 2000ms)
 * @returns Function to stop monitoring
 */
export const startMediaMonitoring = (intervalMs: number = 2000): (() => void) => {
  console.log('🎬 Starting media element monitoring...');
  
  const interval = setInterval(() => {
    const { videos, audios } = countMediaElements();
    console.log(`📊 Active elements - Videos: ${videos}, Audios: ${audios}`);
    
    if (videos > 10) {
      console.warn(`⚠️ High video element count: ${videos} (safe limit: 10)`);
    }
    
    if (videos > 20) {
      console.error(`🚨 CRITICAL: ${videos} video elements detected! WebMediaPlayer overflow likely.`);
    }
  }, intervalMs);

  // Return cleanup function
  return () => {
    clearInterval(interval);
    console.log('🛑 Media element monitoring stopped');
  };
};
